/**
 * Η «ανάγνωση» της σάλας — κοινή για το ταμείο και το PDA, ώστε ένα τραπέζι να
 * σημαίνει το ίδιο πράγμα και στις δύο οθόνες.
 *
 * Αρχή: το χρώμα είναι προσοχή. Το ελεύθερο τραπέζι είναι ήσυχο· γεμάτο χρώμα
 * παίρνει μόνο ό,τι θέλει ενέργεια (κατειλημμένο = κόκκινο, λογαριασμός = κίτρινο).
 */
import { formatEuro } from './formatEuro';

/** Οι καταστάσεις όπως τις βλέπει ο άνθρωπος — πιο λίγες από τις τιμές της βάσης.
 *  Χωρίς «καθαρισμό» από 12/10/2026: το παλιό 'cleaning' διαβάζεται ως ελεύθερο. */
export type FloorTone = 'free' | 'occupied' | 'bill' | 'reserved';

const BILL_STATUSES = ['receipt_printed', 'invoice_printed', 'payment_requested'];

/**
 * @param status          dine_in_tables.status
 * @param hasReservation  επερχόμενη κράτηση πάνω σε (ακόμη) ελεύθερο τραπέζι
 */
export function floorTone(status: string, hasReservation = false): FloorTone {
  if (status === 'occupied') return 'occupied';
  if (BILL_STATUSES.includes(status)) return 'bill';
  if (status === 'reserved' || hasReservation) return 'reserved';
  return 'free';
}

/**
 * Πόση ώρα είναι ανοιχτό το τραπέζι: «5′», «48′», «1:05», «12:40».
 * Κενό όταν λείπει ή είναι άκυρη η ώρα ανοίγματος — καλύτερα τίποτα από «NaN′».
 */
export function formatElapsed(openedAt: string | Date | null | undefined, now: Date | number = Date.now()): string {
  if (!openedAt) return '';
  const start = new Date(openedAt).getTime();
  if (!Number.isFinite(start)) return '';
  // Ρολόι ταμείου λίγο πίσω από τον server: δείξε 0′, όχι αρνητικό.
  const minutes = Math.max(0, Math.floor((new Date(now).getTime() - start) / 60000));
  if (minutes < 60) return `${minutes}′`;
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`;
}

/**
 * «Νέα εμφάνιση» (απόφαση ιδιοκτήτη 12/10/2026): ο χρόνος του τραπεζιού παίρνει χρώμα ΜΟΝΟ όταν
 * περάσει όριο. Τα όρια από 30 ημέρες του GENERIS: γύρος ανά ~23′ διάμεσα (39′ στο 75%), απόδειξη →
 * κλείσιμο 1′ διάμεσα αλλά 16′ στο 90%. Το «έτοιμο από κουζίνα» ΔΕΝ μπαίνει: καμία κουζίνα δεν το
 * σημειώνει σήμερα (0 στις 379 γύρες).
 */
export const BILL_UNPAID_MINUTES = 15;
export const NO_ROUND_MINUTES = 45;

export type AttentionLevel = 'none' | 'idle' | 'bill';

export interface TableAttention {
  /** Από πότε μετρά ο χρόνος που φαίνεται: άνοιγμα, τελευταίος γύρος ή απόδειξη/αίτημα λογαριασμού. */
  basis: 'open' | 'round' | 'receipt';
  elapsed: string;
  /** idle = κατειλημμένο χωρίς νέο γύρο > NO_ROUND_MINUTES· bill = λογαριασμός χωρίς κλείσιμο > BILL_UNPAID_MINUTES. */
  level: AttentionLevel;
}

interface AttentionTable {
  status: string;
  session_opened_at?: string | null;
  /** undefined = παλιός backend χωρίς το πεδίο → συμπεριφορά όπως πριν (χρόνος από το άνοιγμα, χωρίς χρώμα). */
  session_last_order_at?: string | null;
  session_receipt_printed_at?: string | null;
  session_payment_requested_at?: string | null;
}

const minutesSince = (at: string | null | undefined, now: number): number | null => {
  if (!at) return null;
  const start = new Date(at).getTime();
  return Number.isFinite(start) ? Math.max(0, Math.floor((now - start) / 60000)) : null;
};

export function tableAttention(table: AttentionTable, now: Date | number = Date.now()): TableAttention {
  const nowMs = new Date(now).getTime();
  const tone = floorTone(table.status);
  if (tone === 'bill') {
    const at = table.session_receipt_printed_at || table.session_payment_requested_at;
    const minutes = minutesSince(at, nowMs);
    if (minutes !== null) {
      return { basis: 'receipt', elapsed: formatElapsed(at, nowMs), level: minutes > BILL_UNPAID_MINUTES ? 'bill' : 'none' };
    }
  }
  if ((tone === 'occupied' || tone === 'bill') && table.session_last_order_at !== undefined) {
    // Λογαριασμός χωρίς γύρο ακόμη: μετρά από το άνοιγμα.
    const at = table.session_last_order_at || table.session_opened_at;
    const minutes = minutesSince(at, nowMs);
    if (minutes !== null) {
      return {
        basis: table.session_last_order_at ? 'round' : 'open',
        elapsed: formatElapsed(at, nowMs),
        level: tone === 'occupied' && minutes > NO_ROUND_MINUTES ? 'idle' : 'none',
      };
    }
  }
  return { basis: 'open', elapsed: formatElapsed(table.session_opened_at, nowMs), level: 'none' };
}

/** «€38,50» — ίδια μορφή παντού στη σάλα. Άκυρο ποσό → κενό.
 *  Στη «Νέα εμφάνιση» «38,50 €», όπως το Take Away (utils/formatEuro). */
export function formatTableAmount(amount: unknown, look: 'classic' | 'v2' = 'classic'): string {
  const n = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (!Number.isFinite(n)) return '';
  if (look === 'v2') return formatEuro(n);
  return `€${n.toFixed(2).replace('.', ',')}`;
}

export interface FloorSummary {
  counts: Record<FloorTone, number>;
  /** Άθροισμα των ανοιχτών λογαριασμών που φαίνονται στη σάλα. */
  openBalance: number;
}

interface SummaryTable {
  id: number;
  status: string;
  session_total?: number | string | null;
}

export function summarizeFloor(tables: SummaryTable[], reservedTableIds: ReadonlySet<number> = new Set()): FloorSummary {
  const counts: Record<FloorTone, number> = { free: 0, occupied: 0, bill: 0, reserved: 0 };
  let cents = 0;
  tables.forEach(table => {
    counts[floorTone(table.status, reservedTableIds.has(table.id))] += 1;
    const total = parseFloat(String(table.session_total ?? ''));
    // Σε λεπτά: το άθροισμα δεκαδικών (0.1 + 0.2) δεν πρέπει να φανεί στην οθόνη.
    if (Number.isFinite(total)) cents += Math.round(total * 100);
  });
  return { counts, openBalance: cents / 100 };
}
